import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Layout, Code, ShoppingCart, Globe, Sparkles, RefreshCw, Shield } from 'lucide-react';
import { servicesData } from '../mockData';
import { Button } from '../components/ui/button';
import { useSEO } from '../hooks/useSEO';
import JsonLd from '../components/JsonLd';

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = servicesData.find((s) => s.slug === slug);

  const icons = { Layout, Code, ShoppingCart, Globe, Sparkles, RefreshCw, Shield };

  useSEO({
    title: service
      ? `${service.title} | Hei Web Creations Singapore`
      : 'Service | Hei Web Creations',
    description: service
      ? `${service.tagline} ${service.description}`.slice(0, 158)
      : undefined,
  });

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${origin}/services` },
      { '@type': 'ListItem', position: 3, name: service.title, item: `${origin}/services/${service.slug}` },
    ],
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.description,
    provider: {
      '@type': 'Organization',
      name: 'Hei Web Creations Pte Ltd',
      url: origin,
    },
    areaServed: 'Singapore',
    url: `${origin}/services/${service.slug}`,
  };

  const Icon = icons[service.icon];
  const otherServices = servicesData.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div className="pt-32 bg-white" data-testid={`service-detail-${service.slug}`}>
      <JsonLd id="breadcrumb-service" data={breadcrumbSchema} />
      <JsonLd id="service-schema" data={serviceSchema} />
      {/* Hero Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <Link
              to="/services"
              className="inline-flex items-center text-gray-500 hover:text-[#C8102E] transition-colors duration-300 mb-8 text-sm font-medium"
              data-testid="back-to-services-link"
            >
              <ArrowLeft size={16} className="mr-2" />
              All Services
            </Link>

            <div className="w-16 h-16 rounded-2xl bg-[#FAFAFA] flex items-center justify-center mb-8">
              <Icon className="text-[#C8102E]" size={30} />
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              {service.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-500 leading-relaxed">
              {service.tagline}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            {service.longDescription.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-lg md:text-xl text-gray-600 leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              What's included
            </h2>
            <p className="text-lg text-gray-500">
              Everything you get with our {service.title.toLowerCase()} service
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {service.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300"
                data-testid={`feature-${index}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#C8102E]/10 flex items-center justify-center mb-4">
                  <Check className="text-[#C8102E]" size={20} />
                </div>
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">{feature.title}</h3>
                <p className="text-gray-500 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits & Ideal Clients */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-5xl mx-auto">
            {/* Key Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-8 tracking-tight">
                Key Benefits
              </h2>
              <ul className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center text-lg text-gray-600">
                    <div className="w-6 h-6 rounded-full bg-[#C8102E]/10 flex items-center justify-center mr-4 flex-shrink-0">
                      <Check className="text-[#C8102E]" size={14} />
                    </div>
                    {benefit}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Ideal Clients */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-8 tracking-tight">
                Ideal For
              </h2>
              <ul className="space-y-4">
                {service.idealClients.map((client, index) => (
                  <li key={index} className="flex items-center text-lg text-gray-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#C8102E] mr-4 flex-shrink-0" />
                    {client}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto bg-[#1A1A1A] rounded-[2.5rem] px-8 py-20 md:px-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Interested in {service.title}?
            </h2>
            <p className="text-lg md:text-xl text-gray-400 mb-12 leading-relaxed max-w-2xl mx-auto">
              Let's talk about your project. Book a free consultation and get a tailored proposal.
            </p>
            <Link to="/contact">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-block">
                <Button
                  className="bg-[#C8102E] hover:bg-white hover:text-[#1A1A1A] text-white px-10 py-6 text-base rounded-full transition-all duration-300"
                  data-testid="service-detail-cta"
                >
                  Get a Free Consultation
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] tracking-tight">
              Explore other services
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {otherServices.map((other, index) => {
              const OtherIcon = icons[other.icon];
              return (
                <motion.div
                  key={other.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6 }}
                >
                  <Link
                    to={`/services/${other.slug}`}
                    className="group block h-full bg-white rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-all duration-500"
                    data-testid={`other-service-${other.slug}`}
                  >
                    <div className="w-12 h-12 rounded-2xl bg-[#FAFAFA] group-hover:bg-[#C8102E] flex items-center justify-center mb-6 transition-all duration-500">
                      <OtherIcon className="text-[#C8102E] group-hover:text-white transition-colors duration-500" size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-[#1A1A1A] mb-3">{other.title}</h3>
                    <div className="flex items-center text-[#C8102E] font-medium text-sm gap-1 group-hover:gap-2 transition-all duration-300">
                      <span>Learn more</span>
                      <ArrowRight size={16} />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
